import React from "react";

import DataTableColumnHeader from "../../dataTable/DataTableColumnHeader";
import DataTableLinkCell from "../../dataTable/DataTableLinkCell";
import { userEditPath } from "../../routes/routes";

import type { TFunction } from "i18next";

import type { OfferUserRow, PricingPlanRow } from "./offerUserColumns.types";
import type {
  ColumnLayout,
  DataTableColumnDef,
  StatusOption,
  StatusTone,
} from "../../dataTable/dataTable.types";

export const SUBSCRIPTION_STATUS_TONES = {
  unknown: "neutral",
  unpaid: "error",
  canceled: "neutral",
  error: "error",
  incomplete: "error",
  incomplete_expired: "error",
  past_due: "error",
  trialing: "warning",
  active: "success",
  waiting_iap_validation: "warning",
  ended: "neutral",
  processing: "warning",
} as const satisfies Record<string, StatusTone>;

export function offerUserStatusOptions(t: TFunction): StatusOption[] {
  return Object.entries(SUBSCRIPTION_STATUS_TONES).map(([value, tone]) => ({
    value,
    tone,
    label: t(`editor-analytics:subscription_status.${value}`),
  }));
}

/** « prénom nom » : un vrai lien vers la fiche de l'utilisateur, cherché par son texte. */
export function userNameColumn<TData extends OfferUserRow>(
  t: TFunction,
  projectSlug: string | undefined,
): DataTableColumnDef<TData> {
  const title = t("editor-offers:user_name");

  return {
    accessorKey: "user_name",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title={title} />
    ),
    meta: { searchable: true, label: title },
    cell: ({ row }) => (
      <DataTableLinkCell
        href={userEditPath(projectSlug, row.original.user_uuid)}
      >
        {row.original.user_name}
      </DataTableLinkCell>
    ),
  };
}

interface PricingPlanColumnOptions extends ColumnLayout {
  searchable?: boolean;
}

export function pricingPlanColumn<TData extends PricingPlanRow>(
  t: TFunction,
  { searchable = true, ...layout }: PricingPlanColumnOptions = {},
): DataTableColumnDef<TData> {
  const title = t("editor-offers:pricing_plan");

  return {
    accessorKey: "pricing_plan",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title={title} />
    ),
    meta: { ...layout, searchable, label: title },
    cell: ({ row }) => row.original.pricing_plan,
  };
}
